import React, { useEffect } from 'react';
import { useLazyGetCaptchaQuery } from '@/store/api/authApi';
import { useLocation } from 'react-router-dom';

interface CaptchaBoxProps {
  onCaptchaChange: (id: string) => void;
  refreshTrigger?: number; // changes when parent wants to refetch
  autoFetch?: boolean; // optionally allow automatic fetching
}

const CaptchaBox: React.FC<CaptchaBoxProps> = ({
  onCaptchaChange,
  refreshTrigger,
  autoFetch = true,
}) => {
  const { pathname } = useLocation();
  const [getCaptcha, { data, isFetching }] = useLazyGetCaptchaQuery();

  useEffect(() => {
    if (pathname !== '/login') return;
    const interval = setInterval(() => {
      getCaptcha();
    }, 115000);
    return () => clearInterval(interval);
  }, [pathname]);

  // Fetch on mount only if autoFetch = true
  useEffect(() => {
    if (autoFetch) getCaptcha();
  }, [autoFetch, getCaptcha]);

  // Triggered manually when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) getCaptcha();
  }, [refreshTrigger, getCaptcha]);

  // Update parent when new captcha is fetched
  useEffect(() => {
    if (data?.captchaId) onCaptchaChange(data.captchaId);
  }, [data, onCaptchaChange]);

  return (
    <div className="mt-3 flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3">
      {isFetching ? (
        <p className="text-sm text-white/60">Loading captcha...</p>
      ) : data?.image ? (
        <div className="overflow-hidden rounded-xl border border-primary/30 bg-white p-2 shadow-[0_12px_35px_-18px_rgba(255,184,12,0.8)]">
          <img src={data.image} alt="Captcha" className="h-12 w-auto object-contain" />
        </div>
      ) : (
        <p className="text-sm text-white/60">Unable to load captcha.</p>
      )}

      <button
        type="button"
        onClick={() => getCaptcha()}
        className="text-xs font-medium text-primary transition-colors hover:text-primary/80"
      >
        Refresh Captcha
      </button>
    </div>
  );
};

export default CaptchaBox;
